import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const PHONE_TEL = '+919459457733';
const PHONE_DISPLAY = '945 945 7733';
const WHATSAPP =
  'https://wa.me/919459457733?text=Hello%20GoaDeals%2C%20I%20need%20a%20customised%20Goa%20package.';

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Packages', to: '/packages' },
  { label: 'Hotels', to: '/hotels-list' },
  { label: 'Activities', to: '/activities' },
  { label: 'Sightseeing', to: '/tours' },
  { label: 'Why Us', to: '/#why', hash: 'why' },
  { label: 'FAQs', to: '/#faqs', hash: 'faqs' },
  { label: 'Contact', to: '/#contact', hash: 'contact' },
];

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (navRef.current && headerRef.current) {
        // On mobile the nav is an overlay dropdown, so it must not add to the header height
        const navIsOverlay = window.getComputedStyle(navRef.current).position === 'fixed';
        const navHeight = navIsOverlay ? 0 : navRef.current.offsetHeight;
        const headerTopHeight = headerRef.current.querySelector('.header-top')?.offsetHeight || 0;
        const totalHeight = headerTopHeight + navHeight;
        setHeaderHeight(totalHeight);
        document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
      }
    };

    setTimeout(updateHeaderHeight, 100);
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    const observer = new MutationObserver(updateHeaderHeight);
    if (navRef.current) {
      observer.observe(navRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    if (headerRef.current) {
      observer.observe(headerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      observer.disconnect();
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderHidden(true);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderHidden(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeMenu = () => setIsMobileMenuOpen(false);

  const goHash = (hash) => (e) => {
    closeMenu();
    if (window.location.pathname === '/' || window.location.pathname === '/home') {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', `/#${hash}`);
      }
    }
  };

  const isActive = (item) => {
    if (item.hash) return location.hash === `#${item.hash}`;
    const onHome = location.pathname === '/' || location.pathname === '/home';
    if (item.to === '/') return onHome && !location.hash;
    return location.pathname === item.to;
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`header gd-header ${isHeaderHidden ? 'hidden' : ''}`}
        style={{ height: headerHeight > 0 ? `${headerHeight}px` : 'auto' }}
      >
        <div className="container">
          <div className="header-top">
            <div className="header-left">
              <Link to="/" className="logo" aria-label="GoaDeals Home">
                <img src="/images/logo.png" alt="GoaDeals" className="logo-img" />
              </Link>
            </div>

            <div className="header-right">
              <a href={`tel:${PHONE_TEL}`} className="gd-header-phone">
                <span className="gd-phone-badge" aria-hidden="true">
                  <i className="fas fa-phone-alt" />
                </span>
                <span className="gd-phone-text">
                  Call us now <strong>{PHONE_DISPLAY}</strong>
                </span>
              </a>
              <a className="gd-header-book" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                Book Consultation
              </a>
            </div>
          </div>

          <nav ref={navRef} className={`main-nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={isActive(item) ? 'is-active' : ''}
                onClick={item.hash ? goHash(item.hash) : closeMenu}
              >
                {item.label}
              </Link>
            ))}
            <a className="nav-accent" href={WHATSAPP} target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
              Book Consultation
            </a>
            <button type="button" className="login-button nav-login" onClick={() => { closeMenu(); setIsLoginModalOpen(true); }}>
              Login
            </button>
          </nav>

          <button type="button" className="mobile-menu" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menu">
            <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`} />
          </button>
        </div>
      </header>

      {isLoginModalOpen && (
        <div className="modal-overlay" onClick={() => setIsLoginModalOpen(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Login / Sign Up</h3>
              <button type="button" className="close-modal" onClick={() => setIsLoginModalOpen(false)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <form className="login-form">
                <div className="form-group">
                  <input type="email" placeholder="Email" required />
                </div>
                <div className="form-group">
                  <input type="password" placeholder="Password" required />
                </div>
                <button type="submit" className="login-submit">Login</button>
                <p className="signup-link">
                  Don&apos;t have an account? <a href="#signup">Sign Up</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
